// Ficha de registro genérica (§ 3.3, versión de 2 columnas simplificada):
// cabecera con acciones, secciones de información y tarjeta de datos de sistema.
import type { JSX, ReactNode } from "react";
import { Fragment, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { FieldDef, ModuleDef } from "../modules/registry.tsx";
import { LEAD_STATUS_COLOR } from "../modules/registry.tsx";
import { useDelete, useRecord, useUpdate, type Row } from "../lib/resource.ts";
import { useMe } from "../lib/auth.ts";
import { applyDynamicOptions, useCustomFields } from "../lib/customFields.ts";
import { formatDate, formatDateTime, formatMoney } from "../lib/format.ts";
import { sectionIcon } from "../lib/sectionIcons.ts";
import { Button, Card, DotBadge, ErrorState, Loading, Modal } from "./ui.tsx";
import { BusinessUnitBadge } from "./BusinessUnitSelect.tsx";
import { buildPayload, RecordForm, validate, type Values } from "./RecordForm.tsx";
import { toast } from "./toast.tsx";
import { ApiError } from "../lib/api.ts";

export function RecordView(
  { mod, headerExtra, children, aside }:
  { mod: ModuleDef; headerExtra?: (row: Row) => ReactNode; children?: (row: Row) => ReactNode; aside?: (row: Row) => ReactNode },
): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const rec = useRecord(mod.key, id, mod.singular);
  const update = useUpdate(mod.key);
  const del = useDelete(mod.key);
  const customFields = useCustomFields(mod.key);

  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const me = useMe();
  const row = rec.data?.row;
  const readOnly = rec.data?.access === "read" || Boolean(me.data?.readOnly);
  const allFields = useMemo(() => [...applyDynamicOptions(mod.fields, customFields.data ?? []), ...(customFields.data ?? [])], [mod.fields, customFields.data]);
  const customKeys = useMemo(() => new Set((customFields.data ?? []).map((f) => f.name)), [customFields.data]);

  const sections = useMemo(() => {
    const map = new Map<string, FieldDef[]>();
    for (const f of allFields) {
      const s = f.section ?? "General";
      const arr = map.get(s) ?? [];
      arr.push(f);
      map.set(s, arr);
    }
    const entries = [...map.entries()];
    const order = mod.sectionOrder;
    if (order) {
      const idx = (s: string): number => { const i = order.indexOf(s); return i < 0 ? 999 : i; };
      entries.sort((a, b) => idx(a[0]) - idx(b[0]));
    }
    return entries;
  }, [allFields, mod.sectionOrder]);

  if (rec.isLoading) return <Loading />;
  if (rec.isError || !row) return <ErrorState message="No se encontró el registro." onRetry={() => void rec.refetch()} />;

  const cdata = (row["customData"] as Record<string, unknown> | undefined) ?? {};
  const fval = (f: FieldDef): unknown => (f.custom ? cdata[f.name] : row[f.name]);

  const startEdit = (): void => {
    const init: Values = {};
    for (const f of allFields) init[f.name] = (fval(f) as Values[string]) ?? "";
    setValues(init);
    setErrors({});
    setEditing(true);
  };

  const save = (): void => {
    const errs = validate(allFields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    update.mutate({ id: String(row["id"]), body: buildPayload(allFields, values) as Row }, {
      onSuccess: () => { setEditing(false); toast.success("Cambios guardados."); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
    });
  };

  const remove = (): void => {
    if (!window.confirm(`¿Eliminar este registro de ${mod.nav}? Se moverá a la papelera.`)) return;
    del.mutate(String(row["id"]), {
      onSuccess: () => { toast.success("Movido a la papelera."); navigate(`/${mod.key}`); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo eliminar."),
    });
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <button className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-primary transition-colors" onClick={() => navigate(`/${mod.key}`)}>
        <span>←</span> <span className="uppercase tracking-wider">{mod.nav}</span>
      </button>

      {/* Hero */}
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(120deg, rgba(30,58,138,0.06), rgba(59,130,246,0.05) 42%, transparent 72%)" }} />
        <div className="absolute -top-20 -right-12 w-64 h-64 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(30,58,138,0.10), transparent 70%)" }} />
        <div className="relative flex items-start justify-between gap-4 p-5">
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="w-16 h-16 rounded-2xl grid place-items-center text-white font-bold text-xl shrink-0 ring-4 ring-surface"
              style={{ backgroundImage: "linear-gradient(135deg, #1E3A8A, #3B82F6)", boxShadow: "0 10px 26px -8px rgba(30,58,138,0.5)" }}
            >
              {recordInitials(mod.titleField(row))}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-ink tracking-tight truncate">{mod.titleField(row)}</h1>
                {mod.key === "leads" && typeof row["leadStatus"] === "string" && (
                  <DotBadge label={row["leadStatus"] as string} hex={LEAD_STATUS_COLOR[row["leadStatus"] as string]} />
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {metaChips(mod, row).map((c) => (
                  <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/70 dark:bg-slate-800/60 border border-line pl-2 pr-2.5 py-1 text-xs backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span className="text-ink-muted">{c.label}</span>
                    <span className="font-medium text-ink-secondary truncate max-w-[16rem]">{c.value}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerExtra?.(row)}
            {!readOnly && <Button onClick={startEdit}>Editar</Button>}
            {!readOnly && <Button variant="danger" onClick={remove}>Eliminar</Button>}
          </div>
        </div>
      </Card>

      {!editing && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {sections.map(([section, sfields], idx) => (
              <Fragment key={section}>
                <Card className="p-5 transition-shadow hover:shadow-md">
                  <SectionHead section={section} />
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {sfields.map((f) => (
                      <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                        <dt className="dt-label">{f.label}</dt>
                        <dd className="mt-1 dd-value break-words"><Value field={f} v={fval(f)} /></dd>
                      </div>
                    ))}
                  </dl>
                </Card>
                {/* Relacionados (equipo, contactos…) justo debajo de la primera tarjeta de datos. */}
                {idx === 0 && children?.(row)}
              </Fragment>
            ))}
            {sections.length === 0 && children?.(row)}
            <CustomDataCard data={row["customData"]} exclude={customKeys} />
          </div>

          <div className="space-y-4">
            <Card className="p-5 transition-shadow hover:shadow-md">
              <SectionHead section="Sistema" />
              <dl className="space-y-2.5 text-sm">
                <SysRow label="Creado" value={formatDateTime(row["createdAt"] as string)} />
                <SysRow label="Modificado" value={formatDateTime(row["modifiedAt"] as string)} />
                <SysRow label="Última actividad" value={formatDate(row["lastActivityAt"] as string)} />
              </dl>
            </Card>
            {aside?.(row)}
          </div>
        </div>
      )}

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title={`Editar · ${mod.titleField(row)}`}
        wide
        footer={
          <>
            <Button onClick={() => setEditing(false)}>Cancelar</Button>
            <Button variant="primary" onClick={save} disabled={update.isPending}>
              {update.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        <RecordForm fields={allFields} initial={values} errors={errors} onChange={setValues} />
      </Modal>
    </div>
  );
}

/** Muestra los datos de custom_data que NO son campos configurados (para no ocultar info). */
function CustomDataCard({ data, exclude }: { data: unknown; exclude: Set<string> }): JSX.Element | null {
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data as Record<string, unknown>)
    .filter(([k, v]) => k !== "_origen" && !exclude.has(k) && v != null && String(v).trim() !== "");
  if (entries.length === 0) return null;
  return (
    <Card className="p-5 transition-shadow hover:shadow-md">
      <SectionHead section="Información adicional" />
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {entries.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="dt-label">{k.replace(/:$/, "")}</dt>
            <dd className="mt-1 dd-value break-words whitespace-pre-wrap">{String(v)}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function SysRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink-secondary tnum">{value}</dd>
    </div>
  );
}

/** Iniciales para el avatar de la cabecera (máx. 2 letras). */
function recordInitials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const a = words[0]?.[0] ?? "";
  const b = words.length > 1 ? (words[1]?.[0] ?? "") : "";
  return (a + b).toUpperCase();
}

/** Chips de metadatos para el hero: columnas secundarias + claves destacadas. */
function metaChips(mod: ModuleDef, row: Row): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  const cd = (row["customData"] as Record<string, unknown> | undefined) ?? {};
  const push = (label: string, v: unknown): void => {
    if (out.length >= 4) return;
    if (v != null && String(v).trim() !== "" && !out.some((o) => o.value === String(v))) out.push({ label, value: String(v) });
  };
  for (const c of mod.columns) {
    if (c.primary) continue;
    push(c.label, c.custom ? cd[c.name] : row[c.name]);
  }
  push("Grupo", cd["GRUPO_INTERES"]);
  push("Macrosector", cd["MACROSECTOR"]);
  push("Región", cd["REGION"]);
  return out.slice(0, 4);
}

/** Encabezado de sección con chip de icono Lucide monocromático. */
function SectionHead({ section }: { section: string }): JSX.Element {
  const Icon = sectionIcon(section);
  return (
    <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-line">
      <span className="w-8 h-8 rounded-lg grid place-items-center bg-primary/[0.08] text-primary shrink-0">
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <h3 className="sec-title">{section}</h3>
    </div>
  );
}

/** Icono Lucide según el nombre de la sección. */

function Value({ field, v }: { field: FieldDef; v: unknown }): JSX.Element {
  if (v == null || v === "") return <span className="text-ink-muted select-none">—</span>;
  if (field.type === "businessunit") return <BusinessUnitBadge id={String(v)} />;
  if (field.type === "currency") return <span className="font-semibold text-ink tnum">{formatMoney(v as string)}</span>;
  if (field.type === "percent") return <span className="tnum">{`${v}%`}</span>;
  if (field.type === "bool") return (
    <span className={`badge ${v ? "bg-success/10 text-success" : "bg-surface-hover text-ink-secondary"}`}>{v ? "Sí" : "No"}</span>
  );
  if (field.type === "date") return <span className="tnum">{formatDate(v as string)}</span>;
  if (field.type === "url") {
    // Solo enlaces http(s): otro esquema (javascript:, data:…) se muestra como texto.
    const u = String(v);
    const href = /^https?:\/\//i.test(u) ? u : /^[^\s:/]+\.[^\s:]+/.test(u) && !u.includes(":") ? `https://${u}` : null;
    if (!href) return <>{u}</>;
    return <a className="link inline-flex items-center gap-1" href={href} target="_blank" rel="noreferrer">{u}<span className="text-[10px]">↗</span></a>;
  }
  if (field.type === "email") return <a className="link" href={`mailto:${String(v)}`}>{String(v)}</a>;
  return <>{String(v)}</>;
}
